'use client'

import useSWR from 'swr'
import {
  isProposalMetadataLost,
  resolveProposalImageUri,
  resolveProposalUri,
} from '@data/proposals'

export type ProposalMetadataJson = {
  title?: string
  imageUri?: string | null
  proposer?: string
  metadata?: { markdown?: string }[] | string
}

/**
 * Never throws: a dead uri resolves to `null`, which the caller renders as
 * "details unavailable" rather than as an error state. Returning a value also
 * stops SWR from retrying a URL that is never coming back.
 */
const fetchProposalMetadata = async (
  url: string,
): Promise<ProposalMetadataJson | null> => {
  try {
    const response = await fetch(url)
    if (!response.ok) return null
    return (await response.json()) as ProposalMetadataJson
  } catch {
    return null
  }
}

export type UseProposalMetadata = {
  metadata?: ProposalMetadataJson
  /** Image to render, already pointed at this origin when we host it. */
  imageUri?: string
  /** Metadata is gone (lost proposal, or the uri no longer resolves). */
  isUnavailable: boolean
  isLoading: boolean
}

/**
 * Resolves a proposal's metadata URL through the recovery map before fetching:
 * self-hosted copy when we have one, no request at all when the metadata is
 * known to be lost, on-chain uri otherwise (MIP 26 still lives on Supabase).
 */
export function useProposalMetadata(
  address?: string,
  onChainUri?: string,
): UseProposalMetadata {
  const lost = isProposalMetadataLost(address)
  const url = resolveProposalUri(address, onChainUri) || null

  const { data, isLoading } = useSWR(url, fetchProposalMetadata, {
    shouldRetryOnError: false,
  })

  return {
    metadata: data || undefined,
    imageUri: resolveProposalImageUri(data?.imageUri),
    isUnavailable: lost || data === null,
    isLoading: !!url && isLoading,
  }
}

export default useProposalMetadata
